import { useState, useEffect, useCallback, useRef } from 'react';
import type { TimeEntry, BankHoliday } from '../types/timesheet.types';
import { TimesheetStatus, ProjectType, toISODateString } from '../types/timesheet.types';
import type { PendingApprovalWithWeeks, ApprovalSource } from '../types/approval.types';
import { useConnector } from '../contexts/ConnectorContext';
import { WorkflowTriggerType } from '../types/connector.types';
import { getHoursByProjectType } from '../utils/calculations';
import { buildWeeklySummaries } from '../utils/approvalTransform';
import { VALIDATION, SP_LISTS } from '../utils/constants';
import { errorLogger } from '../utils/errorLogger';

interface State {
  pendingItems: PendingApprovalWithWeeks[];
  reviewedItems: PendingApprovalWithWeeks[];
  isLoading: boolean;
  isReviewedLoading: boolean;
  reviewedLoaded: boolean;
  loadError: Error | null;
  isActioning: boolean;
  actionError: Error | null;
}

export interface UseManagerApprovalsResult {
  pendingItems: PendingApprovalWithWeeks[];
  reviewedItems: PendingApprovalWithWeeks[];
  isLoading: boolean;
  isReviewedLoading: boolean;
  loadError: Error | null;
  isActioning: boolean;
  actionError: Error | null;
  approveMonth: (approval: PendingApprovalWithWeeks, approverName: string, approverEmail: string) => Promise<void>;
  rejectMonth: (approval: PendingApprovalWithWeeks, reason: string) => Promise<void>;
  loadReviewed: () => Promise<void>;
  refresh: () => Promise<void>;
}

function listNameFor(source: ApprovalSource): string {
  return source === 'asset' ? SP_LISTS.ASSET_TIMESHEETS : SP_LISTS.TIME_ENTRIES;
}

type PersonLike = { fullName: string; m365Email: string; department: string; id: string };

function buildPersonLookup(people: readonly PersonLike[]): ReadonlyMap<string, PersonLike> {
  const map = new Map<string, PersonLike>();
  for (const p of people) {
    if (p.id) map.set(p.id, p);
    if (p.m365Email) map.set(p.m365Email.toLowerCase(), p);
  }
  return map;
}

function lookupPerson(
  personMap: ReadonlyMap<string, PersonLike>,
  personId: string
): PersonLike | undefined {
  return personMap.get(personId) ?? (personId ? personMap.get(personId.toLowerCase()) : undefined);
}

function groupToApprovalCards(
  entries: readonly TimeEntry[],
  projectTypeMap: ReadonlyMap<string, { type: ProjectType }>,
  personMap: ReadonlyMap<string, PersonLike>,
  source: ApprovalSource,
  isBackupApproval: boolean = false,
  bankHolidays: readonly BankHoliday[] = []
): PendingApprovalWithWeeks[] {
  // For assets, split cards per assetCode so each asset-month is reviewed separately.
  const grouped = new Map<string, TimeEntry[]>();
  for (const entry of entries) {
    const assetKey = source === 'asset' ? entry.assetCode || '(no asset)' : '';
    const key = `${entry.personEmail}::${entry.periodMonth}::${assetKey}`;
    const bucket = grouped.get(key);
    if (bucket) bucket.push(entry);
    else grouped.set(key, [entry]);
  }

  const result: PendingApprovalWithWeeks[] = [];
  for (const [, groupEntries] of grouped) {
    const first = groupEntries[0];
    const person = lookupPerson(personMap, first.personEmail);
    const personName = person?.fullName ?? first.personName;
    const personEmail = person?.m365Email ?? first.personEmail;
    const department = person?.department ?? '';

    result.push({
      personId: person?.id ?? first.personEmail,
      personName,
      personEmail,
      department,
      periodMonth: first.periodMonth,
      totalHours: groupEntries.reduce((s, e) => s + e.hours, 0),
      status: first.status,
      submittedDate: first.submittedDate,
      managerApprovedDate: first.managerApprovedDate,
      breakdownByType: getHoursByProjectType(groupEntries, projectTypeMap),
      weeks: buildWeeklySummaries(groupEntries, projectTypeMap, bankHolidays, first.periodMonth),
      source,
      assetCode: source === 'asset' ? first.assetCode : '',
      isBackupApproval,
    });
  }

  return result.sort((a, b) => (a.submittedDate ?? '').localeCompare(b.submittedDate ?? ''));
}

export function useManagerApprovals(
  managerEmail: string,
  managerName: string
): UseManagerApprovalsResult {
  const connector = useConnector();
  const reviewingRef = useRef(false);

  const [state, setState] = useState<State>({
    pendingItems: [],
    reviewedItems: [],
    isLoading: true,
    isReviewedLoading: false,
    reviewedLoaded: false,
    loadError: null,
    isActioning: false,
    actionError: null,
  });

  const load = useCallback(async () => {
    setState(s => ({ ...s, isLoading: true, loadError: null }));
    try {
      const currentYear = new Date().getUTCFullYear();
      const [allProjects, directReports, backupReports, bhThisYear, bhLastYear] = await Promise.all([
        connector.getAllActiveProjects(),
        connector.getDirectReports(managerEmail),
        connector.getBackupReports(managerEmail),
        connector.getBankHolidays(currentYear),
        connector.getBankHolidays(currentYear - 1),
      ]);
      const bankHolidays: BankHoliday[] = [...bhThisYear, ...bhLastYear];

      const primaryEmailSet = new Set(directReports.map(p => p.m365Email.toLowerCase()));
      const trueBackupReports = backupReports.filter(p => !primaryEmailSet.has(p.m365Email.toLowerCase()));

      const [directEmpArrays, directAstArrays, backupEmpArrays, backupAstArrays] = await Promise.all([
        Promise.all(directReports.map(p => connector.getTimeEntries({ status: TimesheetStatus.Submitted, personEmail: p.m365Email }))),
        Promise.all(directReports.map(p => connector.getTimeEntries({ status: TimesheetStatus.Submitted, personEmail: p.m365Email }, SP_LISTS.ASSET_TIMESHEETS))),
        trueBackupReports.length > 0
          ? Promise.all(trueBackupReports.map(p => connector.getTimeEntries({ status: TimesheetStatus.Submitted, personEmail: p.m365Email })))
          : Promise.resolve([] as TimeEntry[][]),
        trueBackupReports.length > 0
          ? Promise.all(trueBackupReports.map(p => connector.getTimeEntries({ status: TimesheetStatus.Submitted, personEmail: p.m365Email }, SP_LISTS.ASSET_TIMESHEETS)))
          : Promise.resolve([] as TimeEntry[][]),
      ]);

      const projectTypeMap = new Map(allProjects.map(p => [p.projectCode, { type: p.type }]));
      const personMap = buildPersonLookup([...directReports, ...backupReports]);
      const backupPersonMap = buildPersonLookup(trueBackupReports);

      const pendingItems = [
        ...groupToApprovalCards((directEmpArrays as TimeEntry[][]).flat(), projectTypeMap, personMap, 'employee', false, bankHolidays),
        ...groupToApprovalCards((directAstArrays as TimeEntry[][]).flat(), projectTypeMap, personMap, 'asset', false, bankHolidays),
        ...groupToApprovalCards((backupEmpArrays as TimeEntry[][]).flat(), projectTypeMap, backupPersonMap, 'employee', true, bankHolidays),
        ...groupToApprovalCards((backupAstArrays as TimeEntry[][]).flat(), projectTypeMap, backupPersonMap, 'asset', true, bankHolidays),
      ];

      setState(s => ({ ...s, pendingItems, isLoading: false }));
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      errorLogger.log(error, { component: 'useManagerApprovals', action: 'load', userEmail: managerEmail });
      setState(s => ({ ...s, isLoading: false, loadError: error }));
    }
  }, [connector, managerEmail]);

  const loadReviewed = useCallback(async () => {
    if (reviewingRef.current) return;
    setState(s => { if (s.reviewedLoaded) return s; return { ...s, isReviewedLoading: true }; });

    reviewingRef.current = true;
    try {
      const currentYear = new Date().getUTCFullYear();
      const [allProjects, directReports, backupReports, bhThisYear, bhLastYear] = await Promise.all([
        connector.getAllActiveProjects(),
        connector.getDirectReports(managerEmail),
        connector.getBackupReports(managerEmail),
        connector.getBankHolidays(currentYear),
        connector.getBankHolidays(currentYear - 1),
      ]);
      const bankHolidays: BankHoliday[] = [...bhThisYear, ...bhLastYear];

      const primaryEmailSet = new Set(directReports.map(p => p.m365Email.toLowerCase()));
      const trueBackupReports = backupReports.filter(p => !primaryEmailSet.has(p.m365Email.toLowerCase()));

      const [dirEmpApprovedArrays, dirEmpRejectedArrays, dirAstApprovedArrays, dirAstRejectedArrays,
        bakEmpApprovedArrays, bakEmpRejectedArrays, bakAstApprovedArrays, bakAstRejectedArrays] = await Promise.all([
        Promise.all(directReports.map(p => connector.getTimeEntries({ status: TimesheetStatus.ManagerApproved, personEmail: p.m365Email }))),
        Promise.all(directReports.map(p => connector.getTimeEntries({ status: TimesheetStatus.Rejected, personEmail: p.m365Email }))),
        Promise.all(directReports.map(p => connector.getTimeEntries({ status: TimesheetStatus.ManagerApproved, personEmail: p.m365Email }, SP_LISTS.ASSET_TIMESHEETS))),
        Promise.all(directReports.map(p => connector.getTimeEntries({ status: TimesheetStatus.Rejected, personEmail: p.m365Email }, SP_LISTS.ASSET_TIMESHEETS))),
        trueBackupReports.length > 0
          ? Promise.all(trueBackupReports.map(p => connector.getTimeEntries({ status: TimesheetStatus.ManagerApproved, personEmail: p.m365Email })))
          : Promise.resolve([] as TimeEntry[][]),
        trueBackupReports.length > 0
          ? Promise.all(trueBackupReports.map(p => connector.getTimeEntries({ status: TimesheetStatus.Rejected, personEmail: p.m365Email })))
          : Promise.resolve([] as TimeEntry[][]),
        trueBackupReports.length > 0
          ? Promise.all(trueBackupReports.map(p => connector.getTimeEntries({ status: TimesheetStatus.ManagerApproved, personEmail: p.m365Email }, SP_LISTS.ASSET_TIMESHEETS)))
          : Promise.resolve([] as TimeEntry[][]),
        trueBackupReports.length > 0
          ? Promise.all(trueBackupReports.map(p => connector.getTimeEntries({ status: TimesheetStatus.Rejected, personEmail: p.m365Email }, SP_LISTS.ASSET_TIMESHEETS)))
          : Promise.resolve([] as TimeEntry[][]),
      ]);

      const projectTypeMap = new Map(allProjects.map(p => [p.projectCode, { type: p.type }]));
      const personMap = buildPersonLookup([...directReports, ...backupReports]);
      const backupPersonMap = buildPersonLookup(trueBackupReports);

      const reviewedItems = [
        ...groupToApprovalCards(
          [...(dirEmpApprovedArrays as TimeEntry[][]).flat(), ...(dirEmpRejectedArrays as TimeEntry[][]).flat()],
          projectTypeMap, personMap, 'employee', false, bankHolidays
        ),
        ...groupToApprovalCards(
          [...(dirAstApprovedArrays as TimeEntry[][]).flat(), ...(dirAstRejectedArrays as TimeEntry[][]).flat()],
          projectTypeMap, personMap, 'asset', false, bankHolidays
        ),
        ...groupToApprovalCards(
          [...(bakEmpApprovedArrays as TimeEntry[][]).flat(), ...(bakEmpRejectedArrays as TimeEntry[][]).flat()],
          projectTypeMap, backupPersonMap, 'employee', true, bankHolidays
        ),
        ...groupToApprovalCards(
          [...(bakAstApprovedArrays as TimeEntry[][]).flat(), ...(bakAstRejectedArrays as TimeEntry[][]).flat()],
          projectTypeMap, backupPersonMap, 'asset', true, bankHolidays
        ),
      ];

      setState(s => ({ ...s, reviewedItems, isReviewedLoading: false, reviewedLoaded: true }));
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      errorLogger.log(error, { component: 'useManagerApprovals', action: 'loadReviewed', userEmail: managerEmail });
      reviewingRef.current = false;
      setState(s => ({ ...s, isReviewedLoading: false }));
    }
  }, [connector, managerEmail]);

  useEffect(() => { void load(); }, [load]);

  const approveMonth = useCallback(
    async (approval: PendingApprovalWithWeeks, approverName: string, approverEmail: string): Promise<void> => {
      const entries = approval.weeks.flatMap(w => [...w.entries]);
      const now = toISODateString(new Date().toISOString().slice(0, 10));
      const listName = listNameFor(approval.source);

      setState(s => ({ ...s, isActioning: true, actionError: null }));
      try {
        await connector.batchUpdateEntries(
          entries.map(e => ({ id: e.id, fields: {
            status: TimesheetStatus.ManagerApproved,
            managerApprovedDate: now,
            managerName: approverName,
            managerEmail: approverEmail,
          }})),
          listName
        );
        const weekSummary = JSON.stringify(
          approval.weeks.map(w => ({ label: w.label, totalHours: w.totalHours }))
        );
        await connector.triggerWorkflow({
          triggerType: WorkflowTriggerType.ApproveMonth,
          personId: approval.personId,
          userName: approval.personName,
          userEmail: approval.personEmail,
          periodMonth: approval.periodMonth,
          actionStatus: 'Manager Approved',
          isFinalState: false,
          approverName,
          approverEmail,
          approverRole: approval.isBackupApproval ? 'Backup Approver' : 'Manager',
          source: approval.source === 'asset' ? 'AssetTimesheet' : 'ApprovalDashboard',
          totalHours: approval.totalHours,
          weekSummary,
        });
        setState(s => ({
          ...s,
          pendingItems: s.pendingItems.filter(
            a => !(a.personId === approval.personId
              && a.periodMonth === approval.periodMonth
              && a.source === approval.source
              && a.assetCode === approval.assetCode)
          ),
          isActioning: false,
        }));
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        errorLogger.log(error, { component: 'useManagerApprovals', action: 'approveMonth', userEmail: approverEmail });
        setState(s => ({ ...s, isActioning: false, actionError: error }));
        throw error;
      }
    },
    [connector]
  );

  const rejectMonth = useCallback(
    async (approval: PendingApprovalWithWeeks, reason: string): Promise<void> => {
      if (reason.trim().length < VALIDATION.MIN_REJECTION_REASON_LENGTH) {
        throw new Error(`Reason must be at least ${VALIDATION.MIN_REJECTION_REASON_LENGTH} characters.`);
      }

      if (approval.status !== TimesheetStatus.Submitted) {
        throw new Error('Can only reject a Submitted timesheet.');
      }

      const entries = approval.weeks.flatMap(w => [...w.entries]);
      const listName = listNameFor(approval.source);

      setState(s => ({ ...s, isActioning: true, actionError: null }));
      try {
        await connector.batchUpdateEntries(
          entries.map(e => ({ id: e.id, fields: {
            status: TimesheetStatus.Rejected,
            rejectionNote: reason,
            submittedDate: null,
          }})),
          listName
        );
        await connector.triggerWorkflow({
          triggerType: WorkflowTriggerType.RejectMonth,
          personId: approval.personId,
          userName: approval.personName,
          userEmail: approval.personEmail,
          periodMonth: approval.periodMonth,
          actionStatus: 'Rejected',
          isFinalState: false,
          approverName: managerName,
          approverEmail: managerEmail,
          approverRole: approval.isBackupApproval ? 'Backup Approver' : 'Manager',
          rejectionComment: reason,
          reason,
          source: approval.source === 'asset' ? 'AssetTimesheet' : 'ApprovalDashboard',
          totalHours: approval.totalHours,
        });
        setState(s => ({
          ...s,
          pendingItems: s.pendingItems.filter(
            a => !(a.personId === approval.personId
              && a.periodMonth === approval.periodMonth
              && a.source === approval.source
              && a.assetCode === approval.assetCode)
          ),
          isActioning: false,
        }));
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        errorLogger.log(error, { component: 'useManagerApprovals', action: 'rejectMonth', userEmail: managerEmail });
        setState(s => ({ ...s, isActioning: false, actionError: error }));
        throw error;
      }
    },
    [connector, managerName, managerEmail]
  );

  return {
    pendingItems: state.pendingItems,
    reviewedItems: state.reviewedItems,
    isLoading: state.isLoading,
    isReviewedLoading: state.isReviewedLoading,
    loadError: state.loadError,
    isActioning: state.isActioning,
    actionError: state.actionError,
    approveMonth,
    rejectMonth,
    loadReviewed,
    refresh: load,
  };
}
